import { useRef, useState } from 'react';
import { App, Avatar, Dropdown, Image, Tooltip } from 'antd';
import { CameraOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { api, errorMessage, fileUrl } from '../api/client';
import { palette } from '../app/theme';

const TARGETS = {
  contact: { path: 'contacts', file: 'photo', keys: [['contact'], ['contacts']], noun: 'photo' },
  organization: { path: 'organizations', file: 'logo', keys: [['organization'], ['organizations'], ['contacts']], noun: 'logo' },
  user: { path: 'users', file: 'photo', keys: [['users']], noun: 'photo' },
} as const;

/** Every image format the API can resize (see FilesService). */
export const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,image/avif,image/tiff,image/svg+xml,application/pdf,.jpg,.jpeg,.png,.gif,.webp,.tif,.tiff,.svg,.pdf,.avif';

/**
 * A record's photo (contacts) or logo (organizations), falling back to initials. Click the image to see it enlarged;
 * click the camera badge (or the initials, when there is no image) to upload a new one or remove the current one. The API
 * resizes uploads to 256 px. `onChanged` gets the new file id (null when removed) for state that is not a query (the
 * signed-in user's own photo).
 */
export function PhotoAvatar({ kind, recordId, fileId, fallback, size = 56, onChanged }: { kind: keyof typeof TARGETS; recordId: string; fileId: string | null; fallback: string; size?: number; onChanged?: (fileId: string | null) => void }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const t = TARGETS[kind];
  const refresh = () => t.keys.forEach((k) => void qc.invalidateQueries({ queryKey: k }));

  const upload = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await api.post<Record<string, string>>(`/${t.path}/${recordId}/${t.file}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      refresh();
      onChanged?.(r.data[`${t.file}FileId`] ?? null);
      message.success(`${t.noun[0].toUpperCase()}${t.noun.slice(1)} updated`);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/${t.path}/${recordId}/${t.file}`);
      refresh();
      onChanged?.(null);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const items = [
    { key: 'upload', icon: <UploadOutlined />, label: fileId ? `Upload a new ${t.noun}` : `Upload a ${t.noun}`, onClick: () => input.current?.click() },
    ...(fileId ? [{ key: 'remove', icon: <DeleteOutlined />, danger: true, label: `Remove ${t.noun}`, onClick: () => void remove() }] : []),
  ];
  const avatar = (
    <Avatar
      size={size}
      src={fileUrl(fileId)}
      shape={kind === 'organization' ? 'square' : 'circle'}
      style={{ background: fileId && kind === 'organization' ? '#fff' : palette.primarySoft, color: palette.primary, fontWeight: 600, fontSize: size * 0.36, border: fileId && kind === 'organization' ? `1px solid ${palette.borderStrong}` : undefined }}
    >
      {fallback}
    </Avatar>
  );
  const bare = { padding: 0, border: 0, background: 'none', cursor: 'pointer', borderRadius: kind === 'organization' ? 8 : '50%', display: 'inline-flex' } as const;
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={IMAGE_ACCEPT}
        hidden
        aria-label={`${t.noun} file`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void upload(file);
        }}
      />
      {fileId && <Image src={fileUrl(fileId)} alt={fallback} style={{ display: 'none' }} preview={{ open: previewOpen, onOpenChange: setPreviewOpen }} />}
      <span style={{ position: 'relative', display: 'inline-flex', flex: 'none' }}>
        {fileId ? (
          <Tooltip title={`View ${t.noun}`}>
            <button type="button" aria-label={`View ${t.noun}`} onClick={() => setPreviewOpen(true)} style={{ ...bare, cursor: 'zoom-in' }}>
              {avatar}
            </button>
          </Tooltip>
        ) : (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Tooltip title={`Add a ${t.noun}`}>
              <button type="button" aria-label={`Add a ${t.noun}`} style={bare}>
                {avatar}
              </button>
            </Tooltip>
          </Dropdown>
        )}
        <Dropdown menu={{ items }} trigger={['click']}>
          <Tooltip title={fileId ? `Change ${t.noun}` : `Add a ${t.noun}`}>
            <button
              type="button"
              aria-label={fileId ? `Change ${t.noun}` : `Add a ${t.noun}`}
              style={{ position: 'absolute', right: -2, bottom: -2, width: 22, height: 22, padding: 0, borderRadius: '50%', background: '#fff', border: `1px solid ${palette.borderStrong}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: palette.textSecondary, cursor: 'pointer' }}
            >
              <CameraOutlined />
            </button>
          </Tooltip>
        </Dropdown>
      </span>
    </>
  );
}
